import 'Components/atoms/vm-icon-next/vm-icon-next';
import { TriggerController } from 'Components/controllers/trigger-controller';
import { html, LitElement, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { lockScroll, unlockScroll } from 'Utils/dom';
import Styles from './vm-bottom-sheet.scss';

const INITIAL_HEIGHT_RATIO = 0.65;
const MAX_HEIGHT_RATIO = 1;
const DISMISS_THRESHOLD = 0.3;
const VELOCITY_PROJECT_MS = 300; // ms of momentum projected past release to bias the snap target

/**
 * A mobile bottom sheet component that slides up from the bottom of the screen.
 * Supports drag-to-resize/dismiss via the drag handle, backdrop tap to close,
 * and a close button in the header.
 *
 * @slot - Default slot for the sheet body content.
 * @slot header - Slot for header content displayed next to the close button.
 *
 * @fires vm-close - Fired when the user dismisses the sheet (backdrop tap, close button, or drag dismiss).
 *
 * @example
 * Bind to an external trigger via selector — the sheet wires up the click handler itself:
 * ```html
 * <button id="open-info">More info</button>
 *
 * <vm-bottom-sheet trigger="#open-info">
 *   <span slot="header">More info</span>
 *   <p>Sheet body content here</p>
 * </vm-bottom-sheet>
 * ```
 *
 * @example
 * Control visibility manually via the `open` property — useful when the trigger lives
 * in a parent component or the sheet should open in response to non-click events:
 * ```html
 * <vm-bottom-sheet .open=${this.showSheet} @vm-close=${() => (this.showSheet = false)}>
 *   <span slot="header">More info</span>
 *   <p>Sheet body content here</p>
 * </vm-bottom-sheet>
 * ```
 */
@customElement('vm-bottom-sheet')
export class VmBottomSheet extends LitElement {
    static override styles = Styles;

    /** Whether the bottom sheet is visible. */
    @property({ type: Boolean, reflect: true })
    open = false;

    /** Hides the backdrop and disables close-on-outside-click. */
    @property({ type: Boolean, reflect: true, attribute: 'disable-backdrop' })
    disableBackdrop = false;

    /** Initial height in pixels. Falls back to 65% of the viewport if not set. */
    @property({ type: Number, attribute: 'initial-height' })
    initialHeight?: number;

    /**
     * CSS selector for an external element that opens the sheet on click.
     * The element must exist in the DOM when the sheet connects — binding
     * is one-shot, not observed.
     */
    @property()
    trigger?: string;

    @state()
    private _closing = false;

    @state()
    private _sheetHeight = 0;

    @state()
    private _dragging = false;

    @query('.bottom-sheet__sheet')
    private _sheet!: HTMLDivElement;

    private _dragState: {
        startY: number;
        startHeight: number;
        startTime: number;
        currentY: number;
    } | null = null;

    private _triggerCtrl = new TriggerController(this, {
        getSelector: () => this.trigger,
        onActivate: () => {
            this.open = true;
        },
    });

    override connectedCallback(): void {
        super.connectedCallback();
        this._handleKeyDown = this._handleKeyDown.bind(this);
    }

    override updated(changed: Map<PropertyKey, unknown>): void {
        if (changed.has('open')) {
            this._triggerCtrl.setExpanded(this.open);
            if (this.open) {
                this._closing = false;
                this._sheetHeight = this.initialHeight || window.innerHeight * INITIAL_HEIGHT_RATIO; // Treat initialHeight set to 0 as "not set" to avoid rendering the sheet with 0 height.
                this._lockBodyScroll();
                document.addEventListener('keydown', this._handleKeyDown);
            } else if (changed.get('open') === true) {
                // Only unlock on a real true→false transition — skip the initial
                // undefined→false render so we don't clobber another overlay's lock.
                this._unlockBodyScroll();
                document.removeEventListener('keydown', this._handleKeyDown);
                // Dispatch vm-close from here so it fires on every close path,
                // including programmatic open=false (not just the animated close path).
                this.dispatchEvent(new CustomEvent('vm-close', { bubbles: true, composed: true }));
                // Restore focus to the trigger so keyboard/SR users don't lose their place.
                this._triggerCtrl.focus();
            }
        }
    }

    override disconnectedCallback(): void {
        super.disconnectedCallback();
        // Only unlock if the sheet was actually open — otherwise we'd clobber
        // the scroll-lock state of any concurrently-open overlay.
        if (this.open) this._unlockBodyScroll();
        document.removeEventListener('keydown', this._handleKeyDown);
    }

    private _lockBodyScroll(): void {
        lockScroll();
    }

    private _unlockBodyScroll(): void {
        unlockScroll();
    }

    private _handleKeyDown(e: KeyboardEvent): void {
        if (e.key === 'Escape') {
            this._animateClose();
        }
    }

    private _animateClose(): void {
        this._closing = true;
    }

    private _onAnimationEnd(): void {
        if (this._closing) {
            // Setting open=false triggers updated(), which handles vm-close, focus,
            // and scroll-unlock — single source of truth for the close lifecycle.
            this.open = false;
        }
    }

    private _onBackdropClick(): void {
        if (!this.disableBackdrop) {
            this._animateClose();
        }
    }

    private _onSheetClick(e: Event): void {
        e.stopPropagation();
    }

    private _onTouchStart(e: TouchEvent): void {
        const sheet = this._sheet;
        const touch = e.touches[0];
        if (!sheet || !touch) return;
        this._dragState = {
            startY: touch.clientY,
            startHeight: sheet.getBoundingClientRect().height,
            startTime: Date.now(),
            currentY: touch.clientY,
        };
        this._dragging = true;
    }

    private _onTouchMove(e: TouchEvent): void {
        const state = this._dragState;
        const sheet = this._sheet;
        const touch = e.touches[0];
        if (!state || !sheet || !touch) return;
        state.currentY = touch.clientY;

        const deltaY = state.currentY - state.startY;
        const newHeight = Math.min(window.innerHeight * MAX_HEIGHT_RATIO, Math.max(100, state.startHeight - deltaY));
        this._sheetHeight = newHeight;
    }

    private _onTouchEnd(): void {
        const state = this._dragState;
        const sheet = this._sheet;
        if (!state || !sheet) return;

        this._dragging = false;

        const deltaY = state.currentY - state.startY;
        const elapsed = Date.now() - state.startTime;
        const velocity = elapsed > 0 ? deltaY / elapsed : 0; // px/ms, positive = dragged down
        const initialHeight = this.initialHeight ?? window.innerHeight * INITIAL_HEIGHT_RATIO;
        const maxHeight = window.innerHeight * MAX_HEIGHT_RATIO;
        const currentHeight = state.startHeight - deltaY;
        // Project where the height would settle by carrying the release velocity forward;
        // a fast downward flick lands lower (closer to dismiss / initial), a fast upward flick lands higher.
        const projectedHeight = currentHeight - velocity * VELOCITY_PROJECT_MS;

        if (projectedHeight < initialHeight * (1 - DISMISS_THRESHOLD)) {
            // Dismiss via drag — animate height to 0, then fire close
            this._sheetHeight = 0;
            const handler = () => {
                // updated() will dispatch vm-close, restore focus, and unlock scroll.
                this.open = false;
                sheet.removeEventListener('transitionend', handler);
            };
            sheet.addEventListener('transitionend', handler);
        } else {
            // Snap to whichever of the two allowed heights is closer to where the drag would settle
            const distToInitial = Math.abs(projectedHeight - initialHeight);
            const distToMax = Math.abs(projectedHeight - maxHeight);
            this._sheetHeight = distToInitial <= distToMax ? initialHeight : maxHeight;
        }

        this._dragState = null;
    }

    override render() {
        if (!this.open) return nothing;

        return html`
            <div
                class="bottom-sheet__overlay ${this._closing ? 'bottom-sheet__overlay--closing' : ''}"
                @click=${this._onBackdropClick}
            >
                ${this.disableBackdrop ? nothing : html`<div class="bottom-sheet__backdrop"></div>`}
                <div
                    class="bottom-sheet__sheet ${this._closing ? 'bottom-sheet__sheet--closing' : ''}"
                    role="dialog"
                    aria-modal="${!this.disableBackdrop}"
                    aria-labelledby="vm-bottom-sheet-title"
                    style="height: ${this._sheetHeight}px; ${this._dragging ? 'transition: none;' : ''}"
                    @click=${this._onSheetClick}
                    @animationend=${this._onAnimationEnd}
                >
                    <!-- Header (draggable) -->
                    <div
                        class="bottom-sheet__header"
                        @touchstart=${this._onTouchStart}
                        @touchmove=${this._onTouchMove}
                        @touchend=${this._onTouchEnd}
                    >
                        <div class="bottom-sheet__drag-handle"></div>
                        <div class="bottom-sheet__header-row">
                            <div id="vm-bottom-sheet-title" class="bottom-sheet__header-title">
                                <slot name="header"></slot>
                            </div>
                            <button class="bottom-sheet__close-btn" @click=${this._animateClose} aria-label="Close">
                                <vm-icon-next name="x-close" size="md"></vm-icon-next>
                            </button>
                        </div>
                    </div>

                    <!-- Scrollable body -->
                    <div class="bottom-sheet__body">
                        <slot></slot>
                    </div>
                </div>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-bottom-sheet': VmBottomSheet;
    }
    interface HTMLElementEventMap {
        'vm-close': CustomEvent;
    }
}
